import React, {useCallback} from 'react';
import {useNavigate} from 'react-router';
import {useSetMergeSuggestionQueue} from 'services/mergeSuggestions';
import {
  removeMergeSuggestionFromQueue,
  selectMergeSuggestionQueue,
  selectNextMergeSuggestionInQueue,
} from 'services/mergeSuggestions/mergeSuggestionQueueSlice';
import {
  useGetMergeSuggestionByTypeSourceIdAndTargetIdQuery,
  useMarkMergeSuggestionAsProcessedMutation,
} from 'services/mergeSuggestions/mergeSuggestionsApi';
import {useAppDispatch, useAppSelector} from 'store/hooks';
import {ActionButton, FlexBox, NextButton} from 'components';
import {useCompareEntitiesContext} from '../context/CompareEntitiesContext';

export const CompareEntitiesActions = () => {
  const navigate = useNavigate();
  const {onSave, target, source, type} = useCompareEntitiesContext();
  const queue = useAppSelector(selectMergeSuggestionQueue);
  const setQueue = useSetMergeSuggestionQueue();
  const dispatch = useAppDispatch();

  const {data: currentMergeSuggestion} =
    useGetMergeSuggestionByTypeSourceIdAndTargetIdQuery(
      {
        type: type,
        sourceId: (target?.data?.id as string | undefined) ?? '',
        targetId: (source?.data?.id as string | undefined) ?? '',
      },
      {
        // Only run this query if we have valid and existing series IDs
        skip: !target?.data?.id || !source?.data?.id,
      },
    );

  const nextMergeSuggestionInQueue = useAppSelector(
    selectNextMergeSuggestionInQueue,
  );
  const [markMergeSuggestionAsProcessedMutation] =
    useMarkMergeSuggestionAsProcessedMutation();

  const markCurrentMergeSuggestionAsProcessed = useCallback(() => {
    if (currentMergeSuggestion) {
      markMergeSuggestionAsProcessedMutation(currentMergeSuggestion.id);
    }
  }, [currentMergeSuggestion, markMergeSuggestionAsProcessedMutation]);

  const closeSuggestionAndContinue = useCallback(() => {
    markCurrentMergeSuggestionAsProcessed();
    if (nextMergeSuggestionInQueue) {
      dispatch(removeMergeSuggestionFromQueue(nextMergeSuggestionInQueue));
      navigate(nextMergeSuggestionInQueue.link);
    } else {
      navigate('/mergeOverview');
    }
  }, [
    dispatch,
    markCurrentMergeSuggestionAsProcessed,
    navigate,
    nextMergeSuggestionInQueue,
  ]);

  return (
    <FlexBox horizontal right width={'100%'} gap={2}>
      <NextButton items={queue} setter={setQueue} />

      <ActionButton
        background={'transparent'}
        title={'Avslå forslag'}
        onClick={() => {
          closeSuggestionAndContinue();
        }}
        /*
          If someone manually adds IDs to the url, then there might not exist a
          suggestion. But we still allow merging on these IDs, but disables
          this button.
       */
        disabled={!currentMergeSuggestion}
      />
      <ActionButton
        title={'Slå sammen'}
        onClick={() => {
          onSave().then(() => {
            closeSuggestionAndContinue();
          });
        }}
      />
    </FlexBox>
  );
};
